/**
 * Frontend mirror of accounting-backend-alt1/customer-invoices/utils/mlExemptionCategories.js
 *
 * Drives the Settings → Företag → Momsstatus picker (Session 4). When the
 * user is on `vat_status='exempt'` they can pick a kategori; the picker
 * auto-fills the existing free-text `vat_exemption_reason` textarea. Free-
 * text editing remains supported post-pick — picker is UX hint only, not a
 * closed enum.
 *
 * Primary-source discipline (per feedback_authoritative_sources_only.md):
 *
 *   ML 2023:200 publishes 10 kap "Generell skatteplikt och undantag" —
 *   chapter heading verifierad mot SFS innehållsförteckning på Riksdagen.
 *   **Exakta §-nummer per kategori har INTE kunnat verifieras primärkälla**
 *   under Session 4 (Skatteverket Rättslig vägledning auth-rejected,
 *   Riksdagen.se trunkerar SFS-vyn före kap 10 detaljer). Skatteverket
 *   fakturering-sidan (verifierad 2026-05-04): "Vid undantag från momsplikt
 *   anser Skatteverket att det är tillräckligt att säljaren anger uppgiften
 *   'undantag från momsplikt' i fakturan." Chapter-level citation räcker.
 *
 * Källor:
 *   - Mervärdesskattelag 2023:200, 10 kap (kapitelrubrik):
 *     https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/mervardesskattelag-2023200_sfs-2023-200/
 *   - Skatteverket — Momslagens regler om fakturering:
 *     https://www.skatteverket.se/foretag/moms/saljavarorochtjanster/momslagensregleromfakturering.4.58d555751259e4d66168000403.html
 *
 * Keep this file in lockstep with the backend module — listed kategorier,
 * ids, labels and suggestedReasonText värdena måste matcha.
 */

export const ML_EXEMPTION_CATEGORIES = Object.freeze([
    Object.freeze({
        id: 'sjukvard',
        label: 'Sjukvård',
        suggestedReasonText: 'Sjukvård enligt mervärdesskattelagen 10 kap',
    }),
    Object.freeze({
        id: 'tandvard',
        label: 'Tandvård',
        suggestedReasonText: 'Tandvård enligt mervärdesskattelagen 10 kap',
    }),
    Object.freeze({
        id: 'social_omsorg',
        label: 'Social omsorg',
        suggestedReasonText: 'Social omsorg enligt mervärdesskattelagen 10 kap',
    }),
    Object.freeze({
        id: 'utbildning',
        label: 'Utbildning',
        suggestedReasonText: 'Utbildning enligt mervärdesskattelagen 10 kap',
    }),
    Object.freeze({
        id: 'idrott',
        label: 'Idrottsverksamhet',
        suggestedReasonText: 'Idrottsverksamhet enligt mervärdesskattelagen 10 kap',
    }),
    Object.freeze({
        id: 'kultur',
        label: 'Kulturell verksamhet',
        suggestedReasonText: 'Kulturell verksamhet enligt mervärdesskattelagen 10 kap',
    }),
    Object.freeze({
        id: 'allmannyttig_ideell',
        label: 'Allmännyttig ideell verksamhet',
        suggestedReasonText: 'Allmännyttig ideell verksamhet enligt mervärdesskattelagen 4 kap 6 §',
    }),
    Object.freeze({
        id: 'finansiella_tjanster',
        label: 'Finansiella tjänster',
        suggestedReasonText: 'Finansiella tjänster enligt mervärdesskattelagen 10 kap',
    }),
    Object.freeze({
        id: 'forsakring',
        label: 'Försäkringstjänster',
        suggestedReasonText: 'Försäkringstjänster enligt mervärdesskattelagen 10 kap',
    }),
    Object.freeze({
        id: 'lotteri_spel',
        label: 'Lotteri och spel',
        suggestedReasonText: 'Lotteri och spel enligt mervärdesskattelagen 10 kap',
    }),
    Object.freeze({
        id: 'fastighetsuthyrning',
        label: 'Fastighetsuthyrning',
        suggestedReasonText: 'Fastighetsuthyrning enligt mervärdesskattelagen 10 kap',
    }),
    Object.freeze({
        id: 'post',
        label: 'Samhällsomfattande posttjänster',
        suggestedReasonText: 'Samhällsomfattande posttjänster enligt mervärdesskattelagen 10 kap',
    }),
]);

/**
 * Look up a kategori's suggested reason text by id. Returns null when id is
 * unknown or empty (caller falls back to whatever the textarea already
 * contains — picker should never CLEAR the textarea).
 */
export function getMlExemptionSuggestion(id) {
    if (!id) return null;
    const found = ML_EXEMPTION_CATEGORIES.find((c) => c.id === id);
    return found ? found.suggestedReasonText : null;
}
